import React, { MutableRefObject, RefObject, useMemo } from 'react';

import { StringHelper, type Tooltip } from '@bryntum/gantt';
import type { BryntumSchedulerPro, BryntumSchedulerProProps } from '@bryntum/gantt-react';

import { SchedulerControls } from 'components/common/bryntumScheduler/SchedulerControlsHook';
import { EventTooltip } from 'components/common/bryntumScheduler/components/tooltips/EventTooltip';
import { ResourceTooltip } from 'components/common/bryntumScheduler/components/tooltips/ResourceTooltip';
import { chooseViewPreset } from 'components/common/bryntumScheduler/utils/date';
import { makeEventRenderer } from 'components/common/bryntumScheduler/utils/renderer/renderer';

const TOOLTIP_MAX_HEIGHT = 500;
const TOOLTIP_MIN_HEIGHT = 350;
const TOOLTIP_MAX_WIDTH = 650;
const TOOLTIP_MIN_WIDTH = 300;

// TYPES that are not documented in bryntum docs in version 5.5.0 but exist in runtime ( might be added on later versions)
type BryntumEventTooltip = Tooltip & {
    forElement?: HTMLElement;
    align: string;
    eventRecord?: { resource?: any };
};

const adjustTooltipDirection = (tooltip: BryntumEventTooltip) => {
    const targetEl = tooltip.activeTarget;
    if (!targetEl) {
        return;
    }

    const { top, bottom } = targetEl.getBoundingClientRect();
    tooltip.align = top < TOOLTIP_MAX_HEIGHT ? 't-b' : 'b-t';
    if (tooltip.align === 't-b' && document.documentElement.clientHeight - bottom < TOOLTIP_MAX_HEIGHT) {
        tooltip.align = 'b-t';
    }
};

export const useSchedulerConfig = (
    startDate: number | undefined,
    endDate: number | undefined,
    controlsRef: MutableRefObject<SchedulerControls>,
    schedulerInstanceRef: RefObject<BryntumSchedulerPro>,
) => {
    const schedulerConfig = useMemo<BryntumSchedulerProProps>(
        () => ({
            viewPreset: chooseViewPreset(startDate, endDate),

            // left side rows tooltip for custom UI
            cellTooltipFeature: {
                tooltipRenderer: ({ record }) => <ResourceTooltip record={record} />,
            },
            // event tooltip for custom UI
            eventTooltipFeature: {
                template: (data) => <EventTooltip data={data} />,
                minWidth: TOOLTIP_MIN_WIDTH,
                maxWidth: TOOLTIP_MAX_WIDTH,
                minHeight: TOOLTIP_MIN_HEIGHT,
                maxHeight: TOOLTIP_MAX_HEIGHT,
                listeners: {
                    beforeShow({ source: tooltip }: { source: BryntumEventTooltip }) {
                        adjustTooltipDirection(tooltip);
                    },
                },
            },

            tickSize: 10,

            // ── LAYOUT ───────────────────────────────────────────────────────────────
            rowHeight: 44,
            barMargin: 14,
            height: '100%',

            // ── LEFT GRID COLUMNS ────────────────────────────────────────────────────
            columns: [{ text: '', field: 'name', width: 280 }],

            // ── CUSTOM FIELDS ON THE EVENT STORE ─────────────────────────────────────
            eventStore: {
                fields: [
                    { name: 'baselineStartDate', type: 'date' },
                    { name: 'baselineEndDate', type: 'date' },
                    // { name: 'forecastStartDate', type: 'date' }, // dont delete!!
                    // { name: 'forecastFinishDate', type: 'date' }, // dont delete!!
                    { name: 'displayEndDate', type: 'date' },
                    { name: 'milestoneWidth', type: 'number' },
                    { name: 'status', type: 'string' },
                    { name: 'percentDone', type: 'number' },
                ],
            },

            // ── MILESTONE RENDERING ──────────────────────────────────────────────────
            milestoneLayoutMode: 'data',
            milestoneTextPosition: 'always-outside',
            milestoneAlign: 'center',

            // ── EVENT STYLE ───────────────────────────────────────────────────────────
            eventStyle: 'plain',
            eventLayout: 'stack',

            // ── FEATURES (OPTIONAL UX TOGGLES) ───────────────────────────────────────
            // In Bryntum v6 the React wrapper no longer recognises a nested `features`
            // prop — each feature must use its own `*Feature` prop name instead.
            percentBarFeature: { allowResize: false, showPercentage: true },
            timeRangesFeature: {
                showCurrentTimeLine: false,
                showHeaderElements: true,
                enableResizing: false,
            },

            // ── SCROLLING & UI FEATURES OF BRYNTUM SCHEDULER  ─────────────────────
            zoomOnMouseWheel: false,
            zoomOnTimeAxisDoubleClick: false,
            scrollable: { overflowX: true, overflowY: true },
            autoCreate: false,
            taskEditFeature: false,
            eventMenuFeature: false,
            scheduleMenuFeature: false,
            cellMenuFeature: false,
            dependenciesFeature: false,
            eventResizeFeature: false,
            eventDragFeature: false,
            eventDragCreateFeature: false,
            scheduleTooltipFeature: false,
            cellEditFeature: false,
            headerMenuFeature: false,
            sortFeature: false,
            timeAxisHeaderMenuFeature: false,

            // ── EVENT RENDERER ────────────────────────────────────────────────────────
            // This runs inside Bryntum's engine, NOT React's reconciler.
            // It returns an HTML string that Bryntum stamps into each bar's DOM node.

            eventRenderer: makeEventRenderer({ schedulerInstanceRef, StringHelper, controlsRef }),
        }),
        [startDate, endDate],
    );

    return {
        schedulerConfig,
    };
};
